import React, { useEffect, useMemo, useState } from 'react';

/**
 * BarPreview - Renders a preview of the shipping bar
 *
 * This component deliberately uses raw inline styles rather than Polaris: it
 * emulates the storefront bar that `backend/static/js/shipping-bar.js` paints
 * with the merchant's own colours, fonts and geometry, so the merchant sees
 * their bar and not an admin-themed approximation of it.
 *
 * Tier rendering, active-tier selection and `${earned}` expansion are
 * implemented twice - here and in the storefront script - so both are asserted
 * against the shared fixture at `src/__fixtures__/tierRenderCases.json`.
 * Any change to the rules below has to move that fixture too.
 */
// Minimum bar height in px - below this the inset progress bar and message
// would overlap / clip.
export const MIN_BAR_HEIGHT = 40;
export const DEFAULT_BAR_HEIGHT = 48;

// `${earned}` joins the cleared tiers' reward labels with this separator. The
// storefront script must join them the same way - the shared fixture's
// `expectedEarned` / `expectedMessage` values assume it.
export const EARNED_SEPARATOR = ', ';

function resolveBarHeight(value) {
  if (value == null || value === 'auto' || value === '') return DEFAULT_BAR_HEIGHT;
  const parsed = parseInt(String(value), 10);
  if (Number.isNaN(parsed)) return DEFAULT_BAR_HEIGHT;
  return Math.max(MIN_BAR_HEIGHT, parsed);
}

/**
 * Normalise a tier list to `[{threshold, reward_type, reward_label, message}]`.
 *
 * Returns `[]` for anything missing or malformed, which is what makes the
 * preview degrade to today's single-threshold rendering rather than throwing
 * while a merchant is mid-edit (X-1). One bad entry rejects the whole list, so
 * the preview never shows a partial journey - the same rule the storefront
 * script's `getTiers()` follows.
 */
export function normalizeTiers(tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return [];

  const normalized = [];
  for (const tier of tiers) {
    if (!tier || typeof tier !== 'object') return [];
    const threshold = Number(tier.threshold);
    if (!Number.isFinite(threshold) || threshold < 0) return [];
    normalized.push({
      threshold,
      reward_type: tier.reward_type || 'free_shipping',
      reward_label: tier.reward_label || '',
      message: tier.message || '',
    });
  }
  return normalized;
}

/**
 * Expand `${earned}` - the reward labels of the tiers the cart has cleared.
 *
 * `all_cleared` (the default) names every cleared tier in threshold order;
 * `highest_only` names just the highest cleared tier. A cleared tier with no
 * reward label contributes nothing. Nothing cleared expands to an empty string.
 */
export function expandEarned(tiers, cartValue, stackingPolicy) {
  const cleared = tiers.filter((tier) => cartValue >= tier.threshold);
  if (cleared.length === 0) return '';

  let chosen = cleared;
  if (stackingPolicy === 'highest_only') {
    const highest = cleared.reduce(
      (best, tier) => (best === null || tier.threshold >= best.threshold ? tier : best),
      null
    );
    chosen = [highest];
  }

  return chosen
    .map((tier) => String(tier.reward_label || '').trim())
    .filter(Boolean)
    .join(EARNED_SEPARATOR);
}

/**
 * Format the remaining time for `${countdown}` as `HH:MM:SS`, prefixed with
 * `Nd ` once more than a day is left.
 *
 * This is the format contract the storefront countdown has to match - a
 * merchant configures against this preview and ships whatever the script
 * renders.
 */
export function formatCountdown(msRemaining) {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

function BarPreview({
  bar,
  cartValue = 0,
  mini = false,
  currencySymbol = '$',
  effectiveThreshold: effectiveThresholdProp,
  tiers: tiersProp,
  tierStackingPolicy = 'all_cleared',
  countdownEndsAt,
  countdownZeroBehavior = 'hide',
  countdownExpiredMessage = '',
  variant = 'announcement',
}) {
  const {
    free_shipping_threshold = 50,
    // eslint-disable-next-line no-template-curly-in-string
    initial_message = 'Free shipping on orders over ${threshold}!',
    // eslint-disable-next-line no-template-curly-in-string
    progress_message = 'Add ${remaining} more for FREE SHIPPING!',
    goal_reached_message = 'You\'ve earned FREE SHIPPING!',
    background_color = '#000000',
    text_color = '#FFFFFF',
    progress_bar_color = '#4CAF50',
    progress_bar_bg_color = '#333333',
    font_family = 'inherit',
    font_size = '14px',
    font_weight = 'normal',
    padding = '12px 20px',
    border_radius = '0px',
    bar_height,
    show_progress_bar = true,
    progress_bar_height = '4px',
    progress_bar_position = 'bottom',
    progress_bar_animated = true,
    background_image_url,
    background_image_opacity = 1,
    show_close_button = true,
    close_button_color = '#FFFFFF',
    tiers: barTiers,
  } = bar || {};

  const isInCart = variant === 'in_cart';

  // Use effectiveThreshold if provided (e.g. converted to display currency), else raw threshold
  const displayThreshold = effectiveThresholdProp != null ? effectiveThresholdProp : free_shipping_threshold;

  // The `tiers` prop wins; falling back to the bar payload's own `tiers` keeps
  // read-only callers (the Dashboard's mini previews) truthful for a multi-tier
  // bar without every caller having to thread the prop. The bar payload emits
  // `tiers` whole or empty, so a single-threshold bar still takes the path
  // below untouched (MT-5). The countdown props are deliberately NOT mirrored
  // this way: a `hide` countdown that has already elapsed renders nothing at
  // all, and a blank card in the bar list is not what the Dashboard wants.
  const tierSource = tiersProp !== undefined ? tiersProp : barTiers;
  const tiers = useMemo(() => normalizeTiers(tierSource), [tierSource]);

  // MT-4: one rate for the whole journey. `effectiveThreshold` is tier 1
  // converted into the display currency, so the ratio it implies is applied to
  // every tier - a mixed-currency track is structurally impossible.
  const conversionRate =
    effectiveThresholdProp != null && Number(free_shipping_threshold) > 0
      ? effectiveThresholdProp / Number(free_shipping_threshold)
      : 1;

  const displayTiers = useMemo(
    () => tiers.map((tier) => ({ ...tier, threshold: tier.threshold * conversionRate })),
    [tiers, conversionRate]
  );

  // Countdown: absolute end instant in, remaining time out. One interval per
  // rendered preview, cleared on unmount and stopped at zero (CD-4).
  const countdownEndMs = useMemo(() => {
    if (!countdownEndsAt) return null;
    const parsed = new Date(countdownEndsAt).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }, [countdownEndsAt]);

  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (countdownEndMs == null) return undefined;
    setNowMs(Date.now());
    const intervalId = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (now >= countdownEndMs) clearInterval(intervalId);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [countdownEndMs]);

  const countdownRemainingMs = countdownEndMs == null ? null : Math.max(0, countdownEndMs - nowMs);
  const countdownExpired = countdownRemainingMs != null && countdownRemainingMs <= 0;

  // Calculate progress and message
  const { message, progress, markerPercents } = useMemo(() => {
    const hasTiers = displayTiers.length > 0;

    // The track spans the whole journey: 0 to the highest threshold.
    const span = hasTiers ? Math.max(...displayTiers.map((tier) => tier.threshold)) : displayThreshold;

    // The active tier is the first one the cart has not cleared; -1 once every
    // tier is cleared. Nothing here latches - it is recomputed from cartValue
    // on every render, so dropping back below a threshold reverts (MT-3).
    const activeIndex = hasTiers ? displayTiers.findIndex((tier) => tier.threshold > cartValue) : -1;
    const allCleared = hasTiers && activeIndex === -1;
    const activeThreshold = hasTiers
      ? (allCleared ? span : displayTiers[activeIndex].threshold)
      : displayThreshold;

    const remaining = Math.max(0, activeThreshold - cartValue);
    const progressPercent = Math.min(100, (cartValue / span) * 100);

    let displayMessage;
    if (cartValue === 0) {
      displayMessage = initial_message;
    } else if (hasTiers) {
      // MT-8 / AC-3b: every tier cleared shows the one all-goals-complete
      // message, not the last tier's progress message over a full track.
      displayMessage = allCleared
        ? goal_reached_message
        : (displayTiers[activeIndex].message || progress_message);
    } else if (cartValue >= displayThreshold) {
      displayMessage = goal_reached_message;
    } else {
      displayMessage = progress_message;
    }

    // CD-3: the bar owns the zero behaviour. `hide` is handled at render time
    // below; `message` swaps the copy in and the counter has already stopped.
    if (countdownExpired && countdownZeroBehavior === 'message' && countdownExpiredMessage) {
      displayMessage = countdownExpiredMessage;
    }

    // Format threshold: keep integers clean, show 2 decimal places for converted values
    const fmtThreshold = Number.isInteger(activeThreshold)
      ? String(activeThreshold)
      : activeThreshold.toFixed(2);

    // Replace placeholders. `${threshold}` and `${remaining}` resolve against
    // the active tier, so the number a shopper reads is the one they are
    // working toward.
    let resolved = (displayMessage == null ? '' : String(displayMessage))
      .replace(/\$\{threshold\}/g, `${currencySymbol}${fmtThreshold}`)
      .replace(/\$\{remaining\}/g, `${currencySymbol}${remaining.toFixed(2)}`)
      .replace(/\$\{cart_value\}/g, `${currencySymbol}${cartValue.toFixed(2)}`);

    // `${earned}` and `${countdown}` are only substituted when the feature
    // behind them is configured. Without tiers or a countdown the token is
    // passed through untouched, exactly as the storefront script does today.
    //
    // The replacement is a FUNCTION, not a string: reward labels are merchant
    // text, and `String.replace` reads `$&`, `$$`, "$`" and `$'` in a
    // replacement string as patterns, so a label containing one would rewrite
    // itself. The storefront script's getMessage() does the same, and
    // `src/__fixtures__/tierRenderCases.json` pins the case for both.
    if (hasTiers) {
      const earned = expandEarned(displayTiers, cartValue, tierStackingPolicy);
      resolved = resolved.replace(/\$\{earned\}/g, () => earned);
    }
    if (countdownRemainingMs != null) {
      resolved = resolved.replace(/\$\{countdown\}/g, formatCountdown(countdownRemainingMs));
    }

    // One marker per intermediate threshold - every tier except the highest,
    // which is the end of the track (n-1 markers for n tiers).
    const markers = hasTiers && span > 0
      ? displayTiers
        .filter((tier) => tier.threshold > 0 && tier.threshold < span)
        .map((tier) => (tier.threshold / span) * 100)
      : [];

    return { message: resolved, progress: progressPercent, markerPercents: markers };
  }, [
    cartValue,
    displayThreshold,
    displayTiers,
    tierStackingPolicy,
    initial_message,
    progress_message,
    goal_reached_message,
    currencySymbol,
    countdownRemainingMs,
    countdownExpired,
    countdownZeroBehavior,
    countdownExpiredMessage,
  ]);

  const resolvedHeight = resolveBarHeight(bar_height);

  // Bar styles
  const barStyle = {
    backgroundColor: background_color,
    color: text_color,
    fontFamily: font_family,
    fontSize: mini ? '12px' : font_size,
    fontWeight: font_weight,
    padding: mini ? '8px 12px' : padding,
    borderRadius: border_radius,
    position: 'relative',
    textAlign: 'center',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // IC-2: the in-cart bar is inline furniture sized to the container it is
    // mounted in - it does not carry the announcement bar's fixed framing.
    minHeight: (mini || isInCart) ? undefined : `${resolvedHeight}px`,
    width: isInCart ? '100%' : undefined,
    boxSizing: 'border-box',
  };

  // Background image layer
  const bgImageStyle = background_image_url ? {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${background_image_url})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: background_image_opacity,
    zIndex: 0,
  } : null;

  // Inset the progress bar so it sits inside the bar with visual breathing
  // room on all sides, regardless of top/bottom placement.
  const INSET_SIDE = mini ? 6 : 10;
  const INSET_EDGE = mini ? 4 : 6;

  const progressBarContainerStyle = {
    position: 'absolute',
    left: `${INSET_SIDE}px`,
    right: `${INSET_SIDE}px`,
    height: progress_bar_height,
    backgroundColor: progress_bar_bg_color,
    borderRadius: '999px',
    overflow: 'hidden',
    ...(progress_bar_position === 'top' ? { top: `${INSET_EDGE}px` } : { bottom: `${INSET_EDGE}px` }),
  };

  const progressBarStyle = {
    height: '100%',
    width: `${progress}%`,
    backgroundColor: progress_bar_color,
    borderRadius: 'inherit',
    transition: progress_bar_animated ? 'width 0.5s ease-out' : 'none',
  };

  // MT-2: one continuous track with the passed tiers marked, rather than a bar
  // that resets to empty at each tier. Markers are painted in the bar's own
  // background colour so they read as notches cut out of the track.
  const tierMarkerStyle = {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '2px',
    marginLeft: '-1px',
    backgroundColor: background_color,
    opacity: 0.75,
  };

  // Close button styles
  const closeButtonStyle = {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: close_button_color,
    cursor: 'pointer',
    fontSize: '18px',
    padding: '4px',
    lineHeight: 1,
    opacity: 0.8,
  };

  // CD-3: at zero, `hide` removes the bar outright.
  if (countdownExpired && countdownZeroBehavior !== 'message') {
    return null;
  }

  return (
    <div style={barStyle}>
      {/* Background Image */}
      {bgImageStyle && <div style={bgImageStyle} />}

      {/* Progress Bar */}
      {show_progress_bar && (
        <div style={progressBarContainerStyle}>
          <div style={progressBarStyle} />
          {markerPercents.map((percent, index) => (
            <div
              key={`tier-marker-${index}`}
              style={{ ...tierMarkerStyle, left: `${percent}%` }}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {message}
      </div>

      {/* Close Button - the in-cart bar has none: it is inline furniture in
          someone else's cart drawer, not a dismissible announcement. */}
      {show_close_button && !mini && !isInCart && (
        <button style={closeButtonStyle} type="button">
          ×
        </button>
      )}
    </div>
  );
}

export default BarPreview;
